import { useEffect, useState } from "preact/hooks"
import useSWR from "swr"
import { Loader2, Plus, Trash2 } from "lucide-react"
import { api_fetch } from "../../../api/client"
import { Button } from "../../../components/ui/button"
import { Checkbox } from "../../../components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { toast } from "../../../components/ui/use-toast"
import { accrual_bucket, parse_patterns } from "./accrual_bank_model"

interface bucket_draft {
  id: string | null
  name: string
  rollup: string
  source: string
  patterns_text: string
  is_default: boolean
  sort_order: number
}

function draft_of(b: accrual_bucket): bucket_draft {
  return { id: b.id, name: b.name, rollup: b.rollup, source: b.source, patterns_text: b.match_patterns.join(", "), is_default: b.is_default, sort_order: b.sort_order }
}

/**
 * The brand's customer tabs: which deduction source feeds each, which retailer names (as they
 * appear on a split) land in it, and which tab is the source's "All Other".
 */
export function AccrualBucketsModal({ open, onClose, onChanged }: { open: boolean; onClose: () => void; onChanged: () => void }) {
  const { data, isLoading, mutate } = useSWR(open ? "accrual-bank-buckets" : null, () => api_fetch<accrual_bucket[]>("/accrual-bank/buckets"))
  const buckets: accrual_bucket[] = data?.ok ? data.value.data : []
  const [drafts, setDrafts] = useState<bucket_draft[]>([])
  const [saving, setSaving] = useState<string | null>(null)

  useEffect(() => {
    setDrafts(buckets.map(draft_of))
  }, [data])

  function update(index: number, patch: Partial<bucket_draft>) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)))
  }

  function add_row() {
    const last = drafts[drafts.length - 1]
    setDrafts((prev) => [
      ...prev,
      { id: null, name: "", rollup: last?.rollup ?? "UNFI", source: last?.source ?? "unfi", patterns_text: "", is_default: false, sort_order: prev.length },
    ])
  }

  async function save(index: number) {
    const d = drafts[index]
    setSaving(d.id ?? `new-${index}`)
    const res = await api_fetch<accrual_bucket>("/accrual-bank/buckets", {
      method: "POST",
      body: {
        id: d.id,
        name: d.name,
        rollup: d.rollup,
        source: d.source,
        match_patterns: parse_patterns(d.patterns_text),
        is_default: d.is_default,
        sort_order: d.sort_order,
      },
    })
    setSaving(null)
    if (!res.ok) {
      toast({ title: "Could not save bucket", description: res.error.message, variant: "destructive" })
      return
    }
    await mutate()
    onChanged()
  }

  async function remove(index: number) {
    const d = drafts[index]
    if (!d.id) {
      setDrafts((prev) => prev.filter((_, i) => i !== index))
      return
    }
    if (!confirm(`Delete bucket "${d.rollup} / ${d.name}" and its accrual entries?`)) return
    const res = await api_fetch(`/accrual-bank/buckets/${d.id}`, { method: "DELETE" })
    if (!res.ok) {
      toast({ title: "Could not delete bucket", description: res.error.message, variant: "destructive" })
      return
    }
    await mutate()
    onChanged()
  }

  return (
    <Dialog open={open} onOpenChange={(is_open: boolean) => !is_open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Customer buckets</DialogTitle>
          <DialogDescription>
            One row per customer tab of your accrual sheet. A coded deduction lands in the bucket whose source matches and whose
            pattern appears in the split's retailer name; with no match it lands in that source's "All Other". Patterns are
            case-insensitive substrings, comma-separated. On UNFI MCB backup a pattern in brackets matches the chain code, e.g.{" "}
            <code>[vit]</code> for NGVC.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div class="flex items-center justify-center py-8 text-gray-500">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            Loading…
          </div>
        )}

        <div class="overflow-y-auto flex-1 pr-1">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-gray-500 text-xs">
                <th class="text-left py-1 pr-2 w-24">Rollup</th>
                <th class="text-left py-1 pr-2 w-36">Customer tab</th>
                <th class="text-left py-1 pr-2 w-24">Source</th>
                <th class="text-left py-1 pr-2">Match patterns</th>
                <th class="text-center py-1 pr-2 w-20">All Other</th>
                <th class="w-28"></th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((d, i) => (
                <tr key={d.id ?? `new-${i}`} class="border-t align-top">
                  <td class="py-1 pr-2">
                    <Input value={d.rollup} onInput={(e: Event) => update(i, { rollup: (e.target as HTMLInputElement).value })} className="h-8" />
                  </td>
                  <td class="py-1 pr-2">
                    <Input value={d.name} onInput={(e: Event) => update(i, { name: (e.target as HTMLInputElement).value })} className="h-8" />
                  </td>
                  <td class="py-1 pr-2">
                    <Input
                      value={d.source}
                      onInput={(e: Event) => update(i, { source: (e.target as HTMLInputElement).value })}
                      className="h-8"
                      placeholder="unfi"
                    />
                  </td>
                  <td class="py-1 pr-2">
                    <Input
                      value={d.patterns_text}
                      onInput={(e: Event) => update(i, { patterns_text: (e.target as HTMLInputElement).value })}
                      className="h-8"
                      placeholder="whole foods, wfm, [wfm]"
                    />
                  </td>
                  <td class="py-1 pr-2 text-center">
                    <Checkbox checked={d.is_default} onCheckedChange={(checked: boolean) => update(i, { is_default: checked === true })} />
                  </td>
                  <td class="py-1 whitespace-nowrap">
                    <Button size="sm" variant="outline" className="h-8" onClick={() => save(i)} disabled={saving !== null || !d.name.trim()}>
                      {saving === (d.id ?? `new-${i}`) ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => remove(i)} title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button variant="outline" size="sm" className="mt-3" onClick={add_row}>
            <Plus className="h-4 w-4 mr-1" />
            Add bucket
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
